import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { useLocalizedPath } from "../components/Header";
import {
  Paintbrush,
  LayoutGrid,
  Square,
  Grid3X3,
  Sparkles,
  Wrench,
  Leaf,
  ArrowRight,
  Mail,
  Phone,
  CheckCircle,
  Package,
  DoorOpen,
  ClipboardList,
} from "lucide-react";

const serviceImages = {
  painting: "https://images.pexels.com/photos/5493669/pexels-photo-5493669.jpeg",
  drywall: "https://images.unsplash.com/photo-1768321901750-f7b96d774456?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTB8MHwxfHNlYXJjaHw0fHxkcnl3YWxsJTIwY29uc3RydWN0aW9uJTIwaW50ZXJpb3IlMjBmcmFtaW5nfGVufDB8fHx8MTc2OTc1OTczM3ww&ixlib=rb-4.1.0&q=85",
  flooring: "https://images.pexels.com/photos/5691493/pexels-photo-5691493.jpeg",
  tiling: "https://images.unsplash.com/photo-1584622650111-993a426fbf0a?w=800&auto=format&fit=crop",
  decorative: "https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=800&auto=format&fit=crop",
  repairs: "https://images.unsplash.com/photo-1617048530929-0edab8608369?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzd8MHwxfHNlYXJjaHwxfHxjYXJwZW50ZXIlMjB0b29scyUyMG9uJTIwdGFibGUlMjB3b29kfGVufDB8fHx8MTc2OTc1OTc1M3ww&ixlib=rb-4.1.0&q=85",
  ecological: "https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=800&auto=format&fit=crop",
  furniture: "https://images.pexels.com/photos/1249611/pexels-photo-1249611.jpeg",
  windowsDoors: "https://images.unsplash.com/photo-1751486403820-7cf45ebec080?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzV8MHwxfHNlYXJjaHwxfHx3aW5kb3clMjBpbnN0YWxsYXRpb24lMjBtb2Rlcm4lMjBob21lfGVufDB8fHx8MTc2OTc3NDA5Mnww&ixlib=rb-4.1.0&q=85",
};

const serviceIcons = {
  painting: Paintbrush,
  drywall: LayoutGrid,
  flooring: Square,
  tiling: Grid3X3,
  decorative: Sparkles,
  repairs: Wrench,
  ecological: Leaf,
  furniture: Package,
  windowsDoors: DoorOpen,
};

export default function Services() {
  const { t } = useTranslation();
  const { getPath } = useLocalizedPath();

  const serviceKeys = ['drywall', 'flooring', 'repairs', 'ecological', 'furniture'];

  // Services with detail pages
  const servicesWithDetailPage = ['drywall', 'flooring', 'repairs', 'ecological'];

  const getDetailPath = (key) => {
    if (key === 'drywall') return getPath('drywall');
    if (key === 'flooring') return getPath('flooring');
    if (key === 'repairs') return getPath('repairs');
    if (key === 'ecological') return getPath('ecological');
    return null;
  };

  const services = serviceKeys.map(key => ({
    key,
    title: t(`services.items.${key}.title`),
    description: t(`services.items.${key}.description`),
    features: t(`services.items.${key}.features`, { returnObjects: true }),
    icon: serviceIcons[key],
    image: serviceImages[key],
    hasDetailPage: servicesWithDetailPage.includes(key),
    detailPath: getDetailPath(key),
  }));

  return (
    <div data-testid="services-page">
      {/* Hero Section */}
      <section className="bg-[#2D3748] py-16 md:py-24">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="max-w-3xl">
            <h1 className="text-4xl md:text-5xl font-bold font-['Manrope'] text-white mb-6">
              {t('services.hero.title')}
            </h1>
            <p className="text-xl text-gray-300 leading-relaxed">
              {t('services.hero.subtitle')}
            </p>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-16 md:py-24 bg-[#F7FAFC]" data-testid="services-grid">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {services.map((service, index) => {
              const CardWrapper = service.hasDetailPage ? Link : 'div';
              const cardProps = service.hasDetailPage 
                ? { to: service.detailPath, className: "block" }
                : {};
              
              return (
                <CardWrapper key={index} {...cardProps}>
                  <Card
                    className={`overflow-hidden border bg-white hover:shadow-xl transition-all duration-300 service-card ${service.hasDetailPage ? 'cursor-pointer hover:border-orange-300' : ''}`}
                    data-testid={`service-card-${index}`}
                  >
                    <div className="grid md:grid-cols-5 gap-0">
                      <div className="md:col-span-2 h-48 md:h-full img-zoom">
                        <img
                          src={service.image}
                          alt={service.title}
                          className="w-full h-full object-cover"
                        />
                      </div>

                      <div className="md:col-span-3 p-6">
                        <div className="flex items-center gap-3 mb-4">
                          <div className="p-2 rounded-lg bg-orange-100 text-orange-500">
                            <service.icon className="h-5 w-5" />
                          </div>
                          <h3 className="text-xl font-semibold font-['Manrope'] text-[#2D3748]">
                            {service.title}
                          </h3>
                          {service.hasDetailPage && (
                            <ArrowRight className="h-5 w-5 text-orange-500 ml-auto" />
                          )}
                        </div>
                        <p className="text-gray-600 mb-4 leading-relaxed">
                          {service.description}
                        </p>
                        <ul className="grid grid-cols-2 gap-2">
                          {Array.isArray(service.features) && service.features.map((feature, featureIndex) => (
                            <li
                              key={featureIndex}
                              className="flex items-center gap-2 text-sm text-gray-600"
                            >
                              <CheckCircle className="h-4 w-4 text-orange-500 flex-shrink-0" />
                              {feature}
                            </li>
                          ))}
                        </ul>
                        {service.hasDetailPage && (
                          <div className="mt-4 pt-4 border-t border-gray-100">
                            <span className="text-orange-500 font-medium text-sm flex items-center gap-2">
                              {t('services.viewDetails')}
                              <ArrowRight className="h-4 w-4" />
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  </Card>
                </CardWrapper>
              );
            })}
          </div>
        </div>
      </section>

      {/* Renovation Process Section */}
      <section className="py-16 md:py-20 bg-white" data-testid="renovation-process-section">
        <div className="container mx-auto px-4 md:px-6 max-w-4xl">
          <div className="text-center mb-10 md:mb-12">
            <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-orange-100 text-orange-500 mb-4">
              <ClipboardList className="h-7 w-7" />
            </div>
            <h2 className="text-2xl md:text-3xl font-bold font-['Manrope'] text-[#2D3748]">
              {t('services.renovationProcess.title')}
            </h2>
          </div>

          <div className="prose prose-lg max-w-none text-gray-700 leading-relaxed">
            <p className="mb-8">{t('services.renovationProcess.intro')}</p>
            
            <div className="space-y-8">
              {t('services.renovationProcess.steps', { returnObjects: true }).map((step, index) => (
                <div key={index} className="flex gap-4">
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-orange-500 text-white flex items-center justify-center font-bold text-sm">
                    {index + 1}
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-[#2D3748] mb-2">{step.title}</h3>
                    <p className="text-gray-600 leading-relaxed whitespace-pre-line">{step.content}</p>
                  </div>
                </div>
              ))}
            </div>

            <p className="mt-8 p-4 bg-gray-50 rounded-lg border-l-4 border-orange-500 text-gray-700 italic">
              {t('services.renovationProcess.conclusion')}
            </p>
          </div>
        </div>
      </section>

      {/* Info Section */}
      <section className="py-16 md:py-20 bg-white">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <Card className="bg-gray-50 border-0">
            <CardContent className="p-8 md:p-12">
              <div className="flex flex-col md:flex-row items-start gap-6">
                <div className="p-3 rounded-xl bg-orange-100 text-orange-500">
                  <ArrowRight className="h-6 w-6" />
                </div>
                <div>
                  <h3 className="text-xl font-semibold font-['Manrope'] text-[#2D3748] mb-3">
                    {t('services.info.title')}
                  </h3>
                  <p className="text-gray-600 mb-4 leading-relaxed">
                    {t('services.info.description')}
                  </p>
                  <Button
                    asChild
                    variant="outline"
                    className="border-[#2D3748] text-[#2D3748] hover:bg-[#2D3748] hover:text-white"
                    data-testid="zulassung-link-button"
                  >
                    <Link to={getPath('licenseFree')}>
                      {t('services.info.learnMore')}
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Link>
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-24 bg-[#2D3748]" data-testid="services-cta">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl text-center">
          <h2 className="text-3xl md:text-4xl font-bold font-['Manrope'] text-white mb-4">
            {t('services.cta.title')}
          </h2>
          <p className="text-lg text-gray-300 mb-8 max-w-2xl mx-auto">
            {t('services.cta.subtitle')}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              asChild
              size="lg"
              className="bg-orange-500 hover:bg-orange-600 text-white text-lg h-14 px-8"
              data-testid="services-cta-email"
            >
              <a href="mailto:info@mtrenovierung.de?subject=Anfrage%20für%20Renovierungsarbeiten">
                <Mail className="mr-2 h-5 w-5" />
                {t('common.sendEmail')}
              </a>
            </Button>
            <Button
              asChild
              size="lg"
              variant="outline"
              className="border-2 border-white text-white hover:bg-white hover:text-[#2D3748] text-lg h-14 px-8 bg-transparent"
              data-testid="services-cta-phone"
            >
              <a href="tel:+4915207684235">
                <Phone className="mr-2 h-5 w-5" />
                {t('common.callNow')}
              </a>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}
